import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Autocomplete,
  Button,
  IconButton,
  InputAdornment,
  Alert,
  Grid,
  Snackbar,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Stack,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import VariantForm from "./VariantForm/VariantForm";
import Calculator from "./ProductForm/Calculator";
import { useNavigate } from "react-router-dom";

function getCSRFToken() {
  const name = "csrftoken";
  const m = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return m ? decodeURIComponent(m[2]) : null;
}

const IVA_OPTIONS = [0, 5, 15, 999];
const TIPOS = ["Color", "Capacidad", "Medida", "Tamaño", "Presentación", "Material", "Combo"];

export default function Variant() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const [productos, setProductos] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [openAuto, setOpenAuto] = useState(false);
  const [producto, setProducto] = useState(null);

  const [tipo, setTipo] = useState("");
  const [valor, setValor] = useState("");
  const [precio, setPrecio] = useState("");
  const [ivaPorc, setIvaPorc] = useState(15);
  const [stockMin, setStockMin] = useState("");
  const [imagen, setImagen] = useState(null);

  const [stockMode, setStockMode] = useState("OWN");
  const [baseVariantId, setBaseVariantId] = useState("");
  const [conversionQty, setConversionQty] = useState("1");

  const [variantes, setVariantes] = useState([]);
  const [variantesExistentes, setVariantesExistentes] = useState([]);
  const [tipoPermitido, setTipoPermitido] = useState("");

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [saving, setSaving] = useState(false);

  const ivaValorPreview = useMemo(() => {
    const p = parseFloat(precio || "0");
    const porc = parseInt(ivaPorc || 0, 10);
    if (!Number.isFinite(p) || porc === 999) return "0.00";
    return (Math.round(p * (porc / 100) * 100) / 100).toFixed(2);
  }, [precio, ivaPorc]);

  const activeStep = useMemo(() => {
    if (!producto) return 0;
    if (!variantes.length) return 1;
    return 2;
  }, [producto, variantes.length]);

  const resumen = useMemo(() => {
    const total = variantes.length;
    const promedio =
      total > 0
        ? (variantes.reduce((acc, v) => acc + Number(v.precio || 0), 0) / total).toFixed(2)
        : "0.00";
    const conImagen = variantes.filter((v) => !!v.imagen).length;
    const propias = variantes.filter((v) => v.stock_mode === "OWN").length;
    const derivadasVariante = variantes.filter((v) => v.stock_mode === "DERIVED_VARIANT").length;
    const derivadasProducto = variantes.filter((v) => v.stock_mode === "DERIVED_PRODUCT").length;
    const derivadas = derivadasVariante + derivadasProducto;
    return { total, promedio, conImagen, propias, derivadas, derivadasVariante, derivadasProducto };
  }, [variantes]);

  useEffect(() => {
    if (busqueda.length < 2 || producto) return;

    let active = true;
    setLoadingSearch(true);

    (async () => {
      try {
        const resp = await fetch(`/proformas/buscar-productos/?q=${encodeURIComponent(busqueda)}`, {
          credentials: "include",
        });
        const { results = [] } = await resp.json();
        if (active) setProductos(results);
      } catch {
        if (active) setProductos([]);
      } finally {
        if (active) setLoadingSearch(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [busqueda, producto]);

  const cargarVariantes = async (code) => {
    try {
      const res = await fetch(`/productos/ver-variantes/?code=${encodeURIComponent(code)}`, {
        credentials: "include",
      });
      const { variants = [] } = await res.json();
      setVariantesExistentes(variants);
      const firstType = variants.length ? variants[0].variant_type : "";
      setTipoPermitido(firstType);
      setTipo(firstType || "");
    } catch {
      setVariantesExistentes([]);
      setTipoPermitido("");
      setTipo("");
    }
  };

  const resetVariantFields = () => {
    setValor("");
    setPrecio("");
    setIvaPorc(tipoPermitido && variantesExistentes[0]?.iva_porcentaje ? variantesExistentes[0].iva_porcentaje : 15);
    setStockMin("");
    setImagen(null);
    setStockMode("OWN");
    setBaseVariantId("");
    setConversionQty("1");
    setError("");
  };

  const onSelect = async (p) => {
    if (!p) return;

    setProducto(p);
    setBusqueda(`${p.name} (${p.code})`);
    setOpenAuto(false);
    setVariantes([]);
    setTipo("");
    setValor("");
    setPrecio("");
    setIvaPorc(15);
    setStockMin("");
    setImagen(null);
    setStockMode("OWN");
    setBaseVariantId("");
    setConversionQty("1");
    await cargarVariantes(p.code);
  };

  const clearProduct = () => {
    setProducto(null);
    setBusqueda("");
    setProductos([]);
    setOpenAuto(false);
    setVariantesExistentes([]);
    setVariantes([]);
    setTipoPermitido("");
    setTipo("");
    setValor("");
    setPrecio("");
    setIvaPorc(15);
    setStockMin("");
    setImagen(null);
    setStockMode("OWN");
    setBaseVariantId("");
    setConversionQty("1");
    setError("");
  };

  const validateVariant = () => {
    if (!producto) {
      setError("Primero selecciona un producto.");
      return false;
    }

    if (!tipo) {
      setError("Selecciona el tipo de variante.");
      return false;
    }

    if (!valor.trim()) {
      setError("Escribe el valor de la variante.");
      return false;
    }

    if (!precio || Number(precio) <= 0) {
      setError("Ingresa un precio válido.");
      return false;
    }

    if (tipoPermitido && tipo !== tipoPermitido) {
      setError(`Este producto solo permite variantes del tipo "${tipoPermitido}".`);
      return false;
    }

    if (!tipoPermitido && variantesExistentes.length > 0 && tipo !== variantesExistentes[0].variant_type) {
      setError(`Este producto ya tiene variantes del tipo "${variantesExistentes[0].variant_type}".`);
      return false;
    }

    if (!tipoPermitido && variantes.length > 0 && tipo !== variantes[0].tipo) {
      setError(`En esta tanda solo puedes usar el tipo "${variantes[0].tipo}".`);
      return false;
    }

    if (
      variantes.some(
        (v) =>
          v.tipo.toLowerCase() === tipo.toLowerCase() &&
          v.valor.trim().toLowerCase() === valor.trim().toLowerCase()
      )
    ) {
      setError("Ya agregaste una variante con ese valor en esta tanda.");
      return false;
    }

    if (
      variantesExistentes.some(
        (v) =>
          v.variant_type.toLowerCase() === tipo.toLowerCase() &&
          v.value.trim().toLowerCase() === valor.trim().toLowerCase()
      )
    ) {
      setError("Ese valor ya existe en las variantes guardadas.");
      return false;
    }

    if (stockMode === "DERIVED_VARIANT") {
      if (!baseVariantId) {
        setError("Selecciona la variante base.");
        return false;
      }

      if (!conversionQty || Number(conversionQty) <= 0) {
        setError("Ingresa una cantidad contenida válida.");
        return false;
      }
    }

    if (stockMode === "DERIVED_PRODUCT") {
      if (!conversionQty || Number(conversionQty) <= 0) {
        setError("Ingresa una cantidad contenida válida.");
        return false;
      }
    }

    return true;
  };

  const addVariant = () => {
    if (!validateVariant()) return;

    setVariantes((v) => [
      ...v,
      {
        id: Date.now(),
        tipo,
        valor: valor.trim(),
        precio: +precio,
        iva_porcentaje: ivaPorc,
        iva_valor: parseFloat(ivaValorPreview),
        stock_minimo: stockMode === "OWN" && stockMin !== "" ? +stockMin : null,
        imagen,
        stock_mode: stockMode,
        base_variant_id: stockMode === "DERIVED_VARIANT" ? baseVariantId : "",
        conversion_quantity: stockMode !== "OWN" ? conversionQty : "1",
      },
    ]);

    if (!tipoPermitido) setTipoPermitido(tipo);
    resetVariantFields();
    setSuccessMessage("Variante añadida a la lista");
  };

  const deleteVariant = (id) => {
    const next = variantes.filter((x) => x.id !== id);
    setVariantes(next);

    if (next.length === 0 && variantesExistentes.length === 0) {
      setTipoPermitido("");
      setTipo("");
    }
  };

  const saveAll = async () => {
    if (!producto) {
      setError("Selecciona un producto.");
      return;
    }

    if (!variantes.length) {
      setError("No hay variantes para guardar.");
      return;
    }

    const csrfToken = getCSRFToken();
    const formData = new FormData();
    formData.append("product_id", producto.id);

    variantes.forEach((v, i) => {
      formData.append(`variants[${i}][variant_type]`, v.tipo);
      formData.append(`variants[${i}][value]`, v.valor);
      formData.append(`variants[${i}][price]`, v.precio);
      formData.append(`variants[${i}][iva_porcentaje]`, v.iva_porcentaje);
      formData.append(`variants[${i}][stock_mode]`, v.stock_mode || "OWN");
      formData.append(`variants[${i}][base_variant_id]`, v.base_variant_id || "");
      formData.append(`variants[${i}][conversion_quantity]`, v.conversion_quantity || "1");

      if (v.stock_minimo !== null && v.stock_minimo !== undefined) {
        formData.append(`variants[${i}][stock_minimo]`, v.stock_minimo);
      }

      if (v.imagen) {
        formData.append(`variants[${i}][image]`, v.imagen);
      }
    });

    try {
      setSaving(true);

      const resp = await fetch("/productos/crear-variantes/", {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRFToken": csrfToken },
        body: formData,
      });

      const data = await resp.json();

      if (!resp.ok) throw new Error(data.error || "Error al guardar variantes");

      setSuccessMessage("Variantes guardadas exitosamente");
      setVariantes([]);
      setStockMode("OWN");
      setBaseVariantId("");
      setConversionQty("1");
      await cargarVariantes(producto.code);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch("/sistema/logout/", { method: "POST", credentials: "include" });
    } catch {}
    localStorage.clear();
    navigate("/login");
  };

  const handleSelect = (component) => {
    if (component === "AdminSection") navigate("/admin-panel/secciones");
    else if (component === "ModalInventario") setSubmenuOpen((s) => !s);
    else if (component === "AdminProduct") navigate("/admin-panel/inventario/productos");
    else if (component === "AdminVariant") navigate("/admin-panel/inventario/variantes");
    else if (component === "AdminProforma") navigate("/admin-panel/proformas");
    else if (component === "AdminClient") navigate("/admin-panel/clientes");
    else if (component === "AdminPromotion") navigate("/admin-panel/promociones");
    else navigate("/admin-panel");
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#eef3f8" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        navigate={navigate}
        handleLogout={handleLogout}
      />

      <Box sx={{ flexGrow: 1 }}>
        <TopBar open={open} setOpen={setOpen} onSelect={handleSelect} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              sx={{
                textTransform: "none",
                bgcolor: "#1565C0",
                "&:hover": { bgcolor: "#0D47A1" },
                borderRadius: 2,
                px: 2.2,
                fontWeight: 700,
                boxShadow: "0 8px 20px rgba(21,101,192,0.22)",
              }}
              onClick={() => navigate(-1)}
            >
              Regresar
            </Button>
          </Box>

          <Paper
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 4,
              boxShadow: "0 10px 35px rgba(15,23,42,0.08)",
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" fontWeight={800} sx={{ color: "#1565C0", mb: 0.5 }}>
                Asistente de variantes
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b" }}>
                Crea variantes normales o presentaciones derivadas como combos, kits, paquetes o cajas.
              </Typography>
            </Box>

            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
              <Step>
                <StepLabel>Seleccionar producto</StepLabel>
              </Step>
              <Step>
                <StepLabel>Configurar variante</StepLabel>
              </Step>
              <Step>
                <StepLabel>Guardar cambios</StepLabel>
              </Step>
            </Stepper>

            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
                {error}
              </Alert>
            )}

            <Grid container spacing={2.5} sx={{ mb: 3 }}>
              <Grid item xs={12} md={8}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 4,
                    borderColor: "#dbe6f3",
                    bgcolor: "#f8fbff",
                    height: "100%",
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                    <Avatar sx={{ bgcolor: "#dbeafe", color: "#1565C0" }}>
                      <SearchIcon />
                    </Avatar>
                    <Box>
                      <Typography fontWeight={800}>Paso 1: busca el producto</Typography>
                      <Typography variant="body2" sx={{ color: "#64748b" }}>
                        Escribe al menos 2 letras del nombre o el código.
                      </Typography>
                    </Box>
                  </Stack>

                  <Autocomplete
                    options={productos}
                    getOptionLabel={(o) => `${o.name} (${o.code})`}
                    inputValue={busqueda}
                    open={openAuto}
                    loading={loadingSearch}
                    onOpen={() => busqueda.length >= 2 && setOpenAuto(true)}
                    onClose={() => setOpenAuto(false)}
                    onInputChange={(_, v, reason) => {
                      if (reason === "input") {
                        setBusqueda(v);
                        setProducto(null);
                        setOpenAuto(v.length >= 2);
                      }
                    }}
                    onChange={(_, v) => onSelect(v)}
                    noOptionsText="No hay resultados"
                    freeSolo
                    disableClearable
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Buscar producto por nombre o código"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ color: "#64748b" }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={clearProduct} edge="end">
                                <CloseIcon />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 4,
                    borderColor: "#dbe6f3",
                    bgcolor: "#f8fbff",
                    height: "100%",
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                    <Avatar sx={{ bgcolor: "#dcfce7", color: "#15803d" }}>
                      <CheckCircleOutlineIcon />
                    </Avatar>
                    <Box>
                      <Typography fontWeight={800}>Regla de tipo</Typography>
                      <Typography variant="body2" sx={{ color: "#64748b" }}>
                        Un producto solo puede tener un tipo de variante.
                      </Typography>
                    </Box>
                  </Stack>

                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Chip
                      label={tipoPermitido ? `Tipo permitido: ${tipoPermitido}` : "Aún no definido"}
                      color={tipoPermitido ? "primary" : "default"}
                      sx={{ borderRadius: 2, fontWeight: 700 }}
                    />

                    {variantesExistentes.length > 0 && (
                      <Chip
                        label={`${variantesExistentes.length} existentes`}
                        sx={{ borderRadius: 2, fontWeight: 700 }}
                      />
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {producto && (
              <VariantForm
                producto={producto}
                tipo={tipo}
                setTipo={setTipo}
                valor={valor}
                setValor={setValor}
                precio={precio}
                setPrecio={setPrecio}
                ivaPorc={ivaPorc}
                setIvaPorc={setIvaPorc}
                stockMin={stockMin}
                setStockMin={setStockMin}
                imagen={imagen}
                setImagen={setImagen}
                variantes={variantes}
                setVariantes={setVariantes}
                variantesExistentes={variantesExistentes}
                tipoPermitido={tipoPermitido}
                resumen={resumen}
                ivaValorPreview={ivaValorPreview}
                addVariant={addVariant}
                deleteVariant={deleteVariant}
                saveAll={saveAll}
                saving={saving}
                TIPOS={TIPOS}
                IVA_OPTIONS={IVA_OPTIONS}
                stockMode={stockMode}
                setStockMode={setStockMode}
                baseVariantId={baseVariantId}
                setBaseVariantId={setBaseVariantId}
                conversionQty={conversionQty}
                setConversionQty={setConversionQty}
              />
            )}
          </Paper>
        </Box>
      </Box>

      <Calculator />

      <Snackbar
        open={!!error}
        autoHideDuration={3500}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="error" sx={{ borderRadius: 3 }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={2500}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success" sx={{ borderRadius: 3 }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}